<template>
  <HeadTitle
    title="Comprobantes de pago"
    description="Lista de tus comprobantes de pago de nómina"
  ></HeadTitle>
  <Divider></Divider>
  <br />
  <Fieldset class="mt-3 w-10 md:w-10 xl:w-7 ml-3 mb-3" :toggleable="true">
    <template #legend>
      <h4><i class="pi pi-filter"></i> Filtros:</h4>
    </template>
    <div class="flex md:flex-row flex-column">
      <div class="flex flex-column ml-4 mb-3">
        <label class="text-base font-medium mb-2">Proceso</label>
        <Dropdown
          v-model="selectedProcess"
          :options="process"
          @change="refreshData"
        />
      </div>
      <div class="flex flex-column ml-4 mb-3">
        <label class="text-base font-medium mb-2">Fecha inicial</label>
        <Calendar
          v-model="startDate"
          placeholder=""
          showIcon
          showButtonBar
          dateFormat="dd/mm/yy"
          locale="Es"
        >
        </Calendar>
      </div>
      <div class="flex flex-column ml-4">
        <label class="text-base font-medium mb-2">Fecha final</label>
        <Calendar
          v-model="endDate"
          placeholder=""
          showIcon
          showButtonBar
          dateFormat="dd/mm/yy"
          locale="Es"
        >
        </Calendar>
      </div>
    </div>
  </Fieldset>
  <div class="lg:col-8 ml-3 flex flex-column">
    <DataTable
      class="tabla mb-5"
      :value="receipts.data"
      paginator
      :rows="pagination.limit"
      :loading="isLoading"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      tableStyle="min-width: 10rem"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      :totalRecords="receipts.totalItems"
      :currentPageReportTemplate="currentPageReportTemplate"
      scrollable
      scrollHeight="600px"
    >
      <template #empty>
        <div class="p-text-center p-text-secondary p-text-italic">
          No se encontraron resultados
        </div>
      </template>
      <Column field="processType" header="Proceso"></Column>
      <Column field="startDate" header="Fecha inicial">
        <template #body="{ data, field }">
          <span>{{ dateWithoutHour(data[field]) }}</span>
        </template>
      </Column>
      <Column field="endDate" header="Fecha final">
        <template #body="{ data, field }">
          <span>{{ dateWithoutHour(data[field]) }}</span>
        </template>
      </Column>
      <Column header="Acciones">
        <template #body="{ data }">
          <div class="actions">
            <Button
              class="action-btn edit p-button-sm"
              :icon="
                isReceiptDownloading(data['closedPayrollId'])
                  ? 'pi pi-spin pi-spinner'
                  : 'pi pi-download'
              "
              :loading="isReceiptDownloading(data['closedPayrollId'])"
              :disabled="isReceiptDownloading(data['closedPayrollId'])"
              v-tooltip.bottom="
                isReceiptDownloading(data['closedPayrollId'])
                  ? 'Descargando...'
                  : 'Descargar'
              "
              @click="
                onPrintReceipt(
                  $event,
                  data['employeeId'],
                  data['closedPayrollId']
                )
              "
              severity="secondary"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
<script lang="ts" setup>
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { dateWithoutHour, toIsoDate } from '@/shared/utils/tranformDate'
  import Receipt from '../models/Receipt'
  import { getReceipts, generateReceipt } from '../services/ReceiptService'
  import { useUserStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import GetPayrollReceiptsDto from '../services/interfaces/GetReceiptsInputDto'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import dayjs from 'dayjs'

  const receipts = reactive({
    data: [] as Receipt[],
    totalItems: 0,
  })

  const pagination = reactive({
    currentPage: 0,
    lastPage: 0,
    limit: 10,
  })

  const isLoading = ref(true)
  const { user } = storeToRefs(useUserStore())
  const startDate = ref()
  const endDate = ref()
  const selectedProcess = ref('Todos')

  const process = ref<string[]>(['Todos'])
  const downloadingReceipts = reactive<Record<string, boolean>>({})
  watch([startDate, endDate], () => {
    refreshData()
  })

  onMounted(async () => {
    isLoading.value = true
    const getReceiptsInput: GetPayrollReceiptsDto = {
      startDate: startDate.value ? toIsoDate(startDate.value) : '',
      endDate: endDate.value ? toIsoDate(endDate.value) : '',
      process:
        selectedProcess.value !== 'Todos' ? selectedProcess.value : 'all',
      employeeId: user.value?.employeeId || '',
    }

    const response = await getReceipts(getReceiptsInput)

    if (response) {
      process.value = [
        ...process.value,
        ...response.data
          .map((r) => r.processType)
          .filter((value, index, self) => self.indexOf(value) === index),
      ]

      receipts.totalItems = response.totalItems
      receipts.data = response.data
    }

    isLoading.value = false
  })

  const onPrintReceipt = async (
    event: any,
    employeeId: string,
    closedPayrollId: string
  ) => {
    downloadingReceipts[closedPayrollId] = true
    try {
      const receiptInBuffer = await generateReceipt({
        employeeId,
        closedId: closedPayrollId,
      })

      if (!receiptInBuffer) {
        return
      }
      const url = window.URL.createObjectURL(receiptInBuffer)
      const link = document.createElement('a')
      link.href = url
      link.download = `receipt_${closedPayrollId}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } finally {
      delete downloadingReceipts[closedPayrollId]
    }
  }

  const refreshData = async () => {
    const getReceiptsInput: GetPayrollReceiptsDto = {
      startDate: startDate.value
        ? dayjs(startDate.value).format('YYYY-MM-DD')
        : '',
      endDate: endDate.value ? dayjs(endDate.value).format('YYYY-MM-DD') : '',
      process:
        selectedProcess.value !== 'Todos' ? selectedProcess.value : 'all',
      employeeId: user.value?.employeeId || '',
    }

    isLoading.value = true
    const response = await getReceipts(getReceiptsInput)
    if (response) {
      receipts.totalItems = response.totalItems
      receipts.data = response.data
    }
    isLoading.value = false
  }

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${receipts.totalItems}`
  })

  const isReceiptDownloading = (id: string) => !!downloadingReceipts[id]
</script>
